import { useState, useEffect } from "react";
import { T, STATE, MARKET, canAccess } from "../../config/theme.js";
import { api } from "../../hooks/useApi.js";
import { Card, SectionTitle } from "../ui/index.jsx";
import { Lock, FileText, Globe, TrendingUp, Layers, DollarSign, Building2, AlertTriangle, Info, Activity } from "lucide-react";

/* ── 카테고리 색상 (새 팔레트에 맞춤) ── */
const CAT_COLORS = {
  "실적": T.accent, "수급": "#0ea5e9", "규제": T.red,
  "업종": T.yellow, "해외": "#8b5cf6", "배당": T.green,
  "공시": "#ec4899", "일반": T.textMuted,
};

/* ── 뉴스 아이템 컴포넌트 ── */
function NewsItem({ item, locked }) {
  if (locked) {
    return (
      <div style={{ padding: "12px 0", borderBottom: `1px solid ${T.border}`, opacity: 0.4, display: "flex", alignItems: "center", gap: 8 }}>
        <Lock size={14} color={T.textMuted} />
        <div style={{ fontSize: 13, color: T.textDim, fontWeight: 600 }}>더 많은 뉴스는 Starter에서 확인할 수 있습니다</div>
      </div>
    );
  }

  const formatDate = (pubDate) => {
    try {
      const d = new Date(pubDate);
      return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,"0")}.${String(d.getDate()).padStart(2,"0")}`;
    } catch { return ""; }
  };

  const catColor = CAT_COLORS[item.category] || CAT_COLORS["일반"];

  return (
    <a
      href={item.link} target="_blank" rel="noopener noreferrer"
      style={{ display: "block", padding: "14px 0", borderBottom: `1px solid ${T.border}`, textDecoration: "none", transition: "background 0.2s" }}
    >
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
        {item.category && item.category !== "일반" && (
          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: catColor + "15", color: catColor, fontWeight: 700 }}>{item.category}</span>
        )}
        {item.relevance != null && item.relevance >= 70 && (
          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: T.green + "15", color: T.green, fontWeight: 700 }}>관련도 높음</span>
        )}
      </div>
      <div style={{ fontSize: 14, color: T.text, fontWeight: 600, lineHeight: 1.5, marginBottom: 6, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
        {item.title}
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <span style={{ fontSize: 11, color: T.accent, fontWeight: 600 }}>{item.source}</span>
        <span className="mono-num" style={{ fontSize: 11, color: T.textMuted }}>{formatDate(item.pubDate)}</span>
      </div>
    </a>
  );
}

export default function DetailContext({ holding, report, m, p, sc, plan, onUpgrade, token }) {
  const [news, setNews] = useState(null);
  const [newsMeta, setNewsMeta] = useState(null);
  const [newsLoading, setNewsLoading] = useState(true);

  useEffect(() => {
    if (!token || !holding.symbol) { setNewsLoading(false); return; }
    setNewsLoading(true);
    api(`/news/${holding.symbol}?name=${encodeURIComponent(holding.name)}&display=10`, {}, token)
      .then(data => { setNews(data.items || []); setNewsMeta(data.meta || null); })
      .catch(e => { console.error("뉴스 로딩 실패:", e); setNews([]); })
      .finally(() => setNewsLoading(false));
  }, [holding.symbol, token]);

  const newsLimit = plan === "free" ? 3 : Infinity;
  const visibleNews = news ? news.slice(0, newsLimit) : [];
  const hasMore = news && news.length > newsLimit;

  return (
    <div>
      {/* ── 관련 뉴스 ── */}
      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <SectionTitle icon="Globe" tip="최근 종목과 관련된 뉴스 헤드라인입니다. 원문 링크를 클릭하면 기사 전체를 볼 수 있습니다">관련 뉴스</SectionTitle>
          {newsMeta && (
            <span style={{
              fontSize: 11, padding: "4px 10px", borderRadius: 6, fontWeight: 700,
              background: newsMeta.frequency === "높음" ? T.red + "15" : newsMeta.frequency === "보통" ? T.yellow + "15" : T.green + "15",
              color: newsMeta.frequency === "높음" ? T.red : newsMeta.frequency === "보통" ? T.yellow : T.green,
            }}>
              7일 <span className="mono-num">{newsMeta.recentCount}</span>건 · {newsMeta.frequency}
            </span>
          )}
        </div>
        {newsLoading ? (
          <div style={{ textAlign: "center", padding: 30, color: T.textDim, fontSize: 13 }}>관련 뉴스를 불러오는 중입니다</div>
        ) : visibleNews.length > 0 ? (
          <div>
            {visibleNews.map((item, i) => <NewsItem key={i} item={item} />)}
            {hasMore && (
              <div onClick={() => onUpgrade?.("news_full")} style={{ marginTop: 12, padding: "14px", background: T.bg, border: `1px solid ${T.accent}25`, borderRadius: 10, textAlign: "center", cursor: "pointer" }}>
                <span style={{ fontSize: 12, color: T.accent, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  <Lock size={14} /> 추가 뉴스 {news.length - newsLimit}건은 Starter에서 확인할 수 있습니다
                </span>
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: 30, color: T.textDim, fontSize: 13 }}>관련 뉴스가 없습니다</div>
        )}
        <div style={{ marginTop: 10, fontSize: 12, color: T.textMuted }}>
          출처: 네이버 뉴스 검색 · 장중 2~3시간 갱신
        </div>
      </Card>

      {/* ── DART 공시 ── */}
      {report.event?.dart_types?.length > 0 && (
        <Card style={{ marginBottom: 20 }}>
          <SectionTitle icon="FileText" tip="금융감독원(DART)에 제출된 공식 문서입니다">DART 공시</SectionTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {report.event.dart_types.map((d, i) => (
              <div key={i} style={{ fontSize: 13, color: T.text, padding: "10px 14px", background: T.bg, borderRadius: 8, display: "flex", alignItems: "center", gap: 10, borderLeft: `3px solid ${T.yellow}` }}>
                <FileText size={14} color={T.yellow} strokeWidth={1.5} />
                {d}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── 업종 대비 분석 ── */}
      {m.sector_name && m.sector_context && (
        <Card style={{ marginBottom: 20, borderLeft: `3px solid ${m.vs_sector > 0 ? T.green : m.vs_sector < 0 ? T.red : T.blue}` }}>
          <SectionTitle icon="Layers" tip="속한 업종 전체와 비교해서, 업종 흐름인지 개별 이슈인지 판단할 수 있습니다">업종 대비 분석</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div style={{ padding: "12px", background: T.bg, borderRadius: 10 }}>
              <div className="label-micro" style={{ marginBottom: 6 }}>소속 업종</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{m.sector_name}</div>
            </div>
            <div style={{ padding: "12px", background: T.bg, borderRadius: 10 }}>
              <div className="label-micro" style={{ marginBottom: 6 }}>업종 등락률</div>
              <div className="mono-num" style={{ fontSize: 16, fontWeight: 700, color: (m.sector_change || 0) >= 0 ? T.green : T.red }}>{(m.sector_change || 0) >= 0 ? "+" : ""}{(m.sector_change || 0).toFixed(2)}%</div>
            </div>
            <div style={{ padding: "12px", background: T.bg, borderRadius: 10 }}>
              <div className="label-micro" style={{ marginBottom: 6 }}>업종 대비</div>
              <div className="mono-num" style={{ fontSize: 16, fontWeight: 700, color: (m.vs_sector || 0) > 0 ? T.green : (m.vs_sector || 0) < 0 ? T.red : T.blue }}>{(m.vs_sector || 0) > 0 ? "+" : ""}{(m.vs_sector || 0).toFixed(2)}%p</div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: T.text, padding: "12px 16px", background: T.bg, borderRadius: 10, display: "flex", alignItems: "center", gap: 10, lineHeight: 1.5 }}>
            <Info size={16} color={m.sector_context.includes("강세") ? T.green : m.sector_context.includes("약세") ? T.red : T.textDim} strokeWidth={2} />
            {m.sector_context === "업종 흐름" && `${holding.name}은(는) ${m.sector_name} 업종 흐름과 함께 움직이고 있습니다. 개별 이슈보다 업종 전체 분위기를 확인하세요.`}
            {m.sector_context === "업종 대비 강세" && `${holding.name}은(는) ${m.sector_name} 업종보다 강하게 움직이고 있습니다. 개별 호재가 있을 수 있습니다.`}
            {m.sector_context === "업종 대비 약세" && `${holding.name}은(는) ${m.sector_name} 업종보다 약하게 움직이고 있습니다. 개별 악재나 수급 문제를 확인해보세요.`}
          </div>
        </Card>
      )}

      {/* ── 시장 환경 (거시 지표) ── */}
      {report.macro && (
        <Card style={{ marginBottom: 20 }}>
          <SectionTitle icon="Globe" tip="시장 지수, 환율, 금리 등 외부 환경 지표입니다">시장 환경</SectionTitle>

          {report.state_market && (
            <div style={{
              padding: "12px 16px", borderRadius: 10, marginBottom: 16, display: "flex", alignItems: "center", gap: 10,
              background: report.state_market === "HOT" ? T.red + "10" : report.state_market === "COLD" ? T.cyan + "10" : T.green + "10",
              border: `1px solid ${report.state_market === "HOT" ? T.red + "20" : report.state_market === "COLD" ? T.cyan + "20" : T.green + "20"}`,
            }}>
              <Activity size={18} color={report.state_market === "HOT" ? T.red : report.state_market === "COLD" ? T.cyan : T.green} strokeWidth={2} />
              <span style={{ fontSize: 13, fontWeight: 700, color: report.state_market === "HOT" ? T.red : report.state_market === "COLD" ? T.cyan : T.green }}>
                시장 온도: {MARKET[report.state_market]?.label || "보통 (안정)"}
              </span>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {(() => {
              const mc = report.macro;
              const items = [
                { label: "KOSPI 추세", Icon: TrendingUp, val: mc.index_mom_5_20, fmt: v => (v > 0 ? "+" : "") + (v?.toFixed?.(1) || v) + "%", clr: v => v > 0 ? T.green : v < -1 ? T.red : T.yellow, desc: v => v > 0 ? "상승 추세" : v < -1 ? "하락 추세" : "보합세" },
                { label: "업종 강도", Icon: Layers, val: mc.sector_rs_20, fmt: v => (v > 0 ? "+" : "") + (v?.toFixed?.(1) || v) + "%p", clr: v => v > 0 ? T.green : v < -1 ? T.red : T.yellow, desc: v => v > 0 ? "시장 대비 강세" : v < -1 ? "시장 대비 약세" : "시장과 비슷" },
                { label: "외국인 흐름", Icon: Globe, val: mc.market_foreign_streak, fmt: v => v > 0 ? `연속 ${v}일 매수` : v < 0 ? `연속 ${Math.abs(v)}일 매도` : "중립", clr: v => v > 0 ? T.green : v < 0 ? T.red : T.yellow, desc: v => v >= 3 ? "강한 매수세" : v <= -3 ? "강한 매도세" : "관망" },
                { label: "환율 (USD/KRW)", Icon: DollarSign, val: mc.usdkrw_5d, fmt: v => (v > 0 ? "+" : "") + (v?.toFixed?.(1) || v) + "%", clr: v => v > 1 ? T.red : v < -1 ? T.green : T.yellow, desc: v => v > 1 ? "원화 약세" : v < -1 ? "원화 강세" : "안정" },
                { label: "금리 변화", Icon: Building2, val: mc.rate_5d_bp, fmt: v => (v > 0 ? "+" : "") + v + "bp", clr: v => v > 5 ? T.red : v < -5 ? T.green : T.yellow, desc: v => v > 5 ? "금리 상승" : v < -5 ? "금리 하락" : "안정" },
                { label: "공포 지수 (VIX)", Icon: AlertTriangle, val: mc.global_risk_5d, fmt: v => (v > 0 ? "+" : "") + (v?.toFixed?.(1) || v) + "%", clr: v => v > 5 ? T.red : v < -5 ? T.green : T.yellow, desc: v => v > 5 ? "불안 확대" : v < -5 ? "안정화" : "보통" },
              ];
              return items.filter(it => it.val !== undefined && it.val !== null).map((it, i) => (
                <div key={i} style={{ background: T.bg, borderRadius: 12, padding: "14px", border: `1px solid ${T.border}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                    <it.Icon size={14} color={T.textMuted} strokeWidth={1.5} />
                    <span className="label-micro">{it.label}</span>
                  </div>
                  <div className="mono-num" style={{ fontSize: 18, fontWeight: 700, color: it.clr(it.val) }}>{it.fmt(it.val)}</div>
                  <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4, fontWeight: 500 }}>{it.desc(it.val)}</div>
                </div>
              ));
            })()}
          </div>

          <div style={{ marginTop: 16, padding: "14px", borderRadius: 10, background: T.bg, border: `1px solid ${T.border}`, display: "flex", gap: 10, alignItems: "flex-start" }}>
            <Info size={16} color={T.textDim} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>
              {(() => {
                const mc = report.macro;
                const neg = [], pos = [];
                if (mc.index_mom_5_20 < -1) neg.push("지수 하락 추세");
                if (mc.index_mom_5_20 > 0) pos.push("지수 상승 중");
                if (mc.usdkrw_5d > 1) neg.push("환율 상승 (원화 약세)");
                if (mc.global_risk_5d > 5) neg.push("글로벌 불안 확대");
                if (mc.market_foreign_streak < -3) neg.push("외국인 연속 매도");
                if (mc.market_foreign_streak > 3) pos.push("외국인 연속 매수");
                if (mc.rate_5d_bp > 5) neg.push("금리 상승 압력");
                if (!neg.length && pos.length) return "시장 환경이 전반적으로 우호적입니다.";
                if (neg.length && !pos.length) return `주의 요인: ${neg.join(", ")}`;
                if (neg.length && pos.length) return `긍정: ${pos.join(", ")} / 주의: ${neg.join(", ")}`;
                return "시장 환경은 보통 수준입니다.";
              })()}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
